import { useLocation } from 'js/lib/useRouter';

import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import {
  useIsEnrolled,
  useIsEnrolledOutsideOfProgram,
  useUserProductInfoLoading,
} from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseUserProductInfoContext';
import { useSpecializationNextSteps } from 'bundles/unified-description-page-common/hooks/useSpecializationNextSteps';
import { useEnterpriseAdminEnrollment } from 'bundles/unified-description-page-common/providers/enterprise/EnterpriseAdminEnrollmentDataProvider';
import { addExternalPlatformTracking } from 'bundles/unified-description-page-common/utils';

const useContinueWithSpecialization = () => {
  const isEnrolled = useIsEnrolled();
  const isLoading = useUserProductInfoLoading();
  const pageData = useEnterpriseSpecializationPageData();
  const isEnrolledOutsideOfProgram = useIsEnrolledOutsideOfProgram();
  const location = useLocation();
  const { deepLinkItemId } = location.query;
  const hasNextCourseLink = !!pageData?.courses?.[0]?.slug;

  const shouldShowGoToCourseButton = !!(
    (isEnrolled || isEnrolledOutsideOfProgram) &&
    !deepLinkItemId &&
    hasNextCourseLink
  );

  return { shouldShowGoToCourseButton, isLoading };
};

export const useAutoContinueWithSpecialization = (isExternalParam: string, s12nId: string | undefined) => {
  const { shouldShowGoToCourseButton, isLoading: isContinueButtonLoading } = useContinueWithSpecialization();
  const { isProgramMember: isMember } = useProgramMembership();
  const isExternal = isExternalParam === 'true';
  const skipNextSteps = !isExternal || !isMember;
  let redirectUrl = null;
  const { resourcePath, loading: isNextStepLoading } = useSpecializationNextSteps(s12nId, skipNextSteps);
  const { isAdminEnrollmentEnabled: isGlobalAutoEnrollmentEnabled } = useEnterpriseAdminEnrollment();
  // Ideally we should skip the next steps API call if we are not showing the continue button,
  // but involves a bit of delay to determine if we are showing the continue button or not, because it involves data from multiple sources
  // Also, it will involve adding useEffect hook to trigger next steps API call after we determine if we are showing the continue button or not
  // Which makes the it a CSR interaction, and we want to avoid that for faster redirect
  // So we are calling the next steps API always only for users coming from LMS/LXPs (isExternal === true)
  // Review discussion for more context - https://github.com/webedx-spark/web/pull/46684#discussion_r1906977748
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const shouldAutoContinueWithCourse = !!(
    shouldShowGoToCourseButton &&
    (features?.enableOptimizedLearnerFlow || isGlobalAutoEnrollmentEnabled)
  );
  if (resourcePath) {
    redirectUrl = addExternalPlatformTracking(resourcePath);
  }

  return {
    resourcePath: isExternal && shouldAutoContinueWithCourse ? redirectUrl : '',
    isLoading: isContinueButtonLoading || isNextStepLoading || isFeaturesLoading,
  };
};

export default useContinueWithSpecialization;
